/**
 * @file tier-discount.api.ts
 * @description Clean API layer for tier discount bundles - follows standard bundle pattern
 */

import { Injectable } from 'framework/di';
import { pipe } from 'framework/functional';

import { BoostAPI } from '../app/app.api';
import { AppService } from '../app/app.service';
import { B2BService } from '../b2b/b2b.service';
import { PlatformLoader } from '../platform/platform.loader';

interface BundleQueryParams extends Record<string, string | number | boolean | undefined> {
  product_id: number;
  shop: string;
  country?: string;
  customer_id?: string | number;
  locale?: string;
}

interface TierDiscountResponse {
  bundles: TierDiscountBundle[];
  meta?: {
    rid?: string;
    money_format?: string;
    money_format_with_currency?: string;
    currency?: string;
  };
}

interface TierDiscountBundle {
  bundle_type: 'tiered';
  widgetId: string;
  discount_value: number;
  discount_type: 'net' | 'percentage' | 'amount';
  bundle_display_type: string;
  updated_at: number;
  threshold_type: 'subtotal' | 'quantity';
  discounts: TierDiscountRule[];
  excludes: any[];
  products: any[];
}

interface TierDiscountRule {
  item: number;
  discount_type: 'percentage' | 'amount';
  discount_value: string;
}

@Injectable
export class TierDiscountAPI extends BoostAPI {
  constructor(
    protected platformLoader: PlatformLoader,
    protected appService: AppService,
    protected b2bService: B2BService
  ) {
    super(platformLoader);
    this.config.baseURL = this.appService.TAEAppConfig?.bundleUrl || '';
  }

  protected buildBundleQueryParams(params: BundleQueryParams): BundleQueryParams {
    return pipe(
      { ...params },
      (p) => this.addCurrencyParams(p),
      (p) => this.b2bService.addB2BRequestParams(p)
    );
  }

  async getTierDiscountBundles(productId: number): Promise<TierDiscountBundle[]> {
    if (!productId) return [];

    const params: BundleQueryParams = {
      product_id: productId,
      shop: this.appService.TAEAppConfig?.shop?.domain || '',
      country: this.platformLoader.platform.localization?.country || '',
      customer_id: this.platformLoader.platform.customer?.id,
    };

    const enhancedParams = this.buildBundleQueryParams(params);
    const filteredParams: Record<string, string | number | boolean> = {};
    for (const [key, value] of Object.entries(enhancedParams)) {
      if (value !== undefined) {
        filteredParams[key] = value;
      }
    }
    const queryParams = this.buildDefaultQueryParams(filteredParams);

    try {
      const response = await this.get<TierDiscountResponse>(`?${queryParams}`);
      if (response.status === 200 && response.data) {
        // Note: Currency formatting is handled by AppService.formatCurrency
        // No need to update currency config here

        // Filter for tier discount bundles only
        return response.data.bundles.filter(
          (bundle) =>
            bundle.bundle_type === 'tiered' &&
            Array.isArray(bundle.products) &&
            bundle.products.length > 0 &&
            Array.isArray(bundle.discounts) &&
            bundle.discounts.length > 0
        );
      }
      return [];
    } catch {
      return [];
    }
  }

  /**
   * Get tier discount bundles from search page dynamicBundles context
   */
  getTierDiscountBundlesFromSearch(): TierDiscountBundle[] {
    const filterBlockData = this.appService.blockByType('filter') as any;
    const dynamicBundles = filterBlockData?.dynamicBundles;

    if (!dynamicBundles || !Array.isArray(dynamicBundles)) {
      return [];
    }

    // Filter for tier discount bundles with validation
    return dynamicBundles.filter(
      (bundle: any) =>
        bundle?.bundle_type === 'tiered' &&
        Array.isArray(bundle.products) &&
        bundle.products.length > 0 &&
        Array.isArray(bundle.discounts) &&
        bundle.discounts.length > 0
    );
  }
}
